import { useAppDispatch } from "@app/hooks";
import { removePOI, updatePOI as updatePOIAction } from "@/features/survey/poisSlice";
import { useDeletePOIMutation } from "@services/survey-poi";
import useSimpleConfirmation from "@hooks/useSimpleConfirmation";
import type { SurveyPOIWithCategory } from "@/features/survey/types/survey.poi.types";

/**
 * Custom hook for POI category validation and warning logic.
 * Provides reusable functions for checking category presence,
 * showing confirmation dialogs, and deleting POIs without categories.
 *
 * Used by BuilderHeader (outside click) and PoiDetailForm (close/back buttons)
 * to maintain consistent behavior across all close triggers.
 */
export const usePoiCategoryWarning = () => {
  const dispatch = useAppDispatch();
  const { confirmAction } = useSimpleConfirmation();
  const [deletePOI] = useDeletePOIMutation();

  /**
   * Check if POI is missing a category
   * @param categoryId - The category ID to validate
   * @returns true if category is missing/empty/whitespace
   */
  const showCategoryWarning = (categoryId: string | undefined): boolean => {
    return !categoryId || categoryId.trim() === "";
  };

  /**
   * Show confirmation dialog for leaving without category
   * @returns Promise<boolean> - true if user clicked "Leave", false if "Stay Here"
   */
  const confirmCategoryWarning = async (): Promise<boolean> => {
    return await confirmAction(
      "Warning",
      "You haven't entered a Category so your POI data will not be saved are you sure you want to leave?",
      {
        icon: "warning",
        confirmButtonText: "Leave",
        cancelButtonText: "Stay Here",
        reverseButtons: true,
        focusCancel: true,
      },
    );
  };

  /**
   * Silently delete POI if no category is assigned.
   * Uses optimistic Redux update with rollback on error.
   *
   * @param categoryId - The category ID to check
   * @param surveyId - The survey ID
   * @param poiId - The POI ID to delete
   * @param currentPOI - The current POI data (for rollback on error)
   */
  const checkAndDeletePOIWithoutCategory = async (
    categoryId: string | undefined,
    surveyId: string,
    poiId: string,
    currentPOI: SurveyPOIWithCategory,
  ): Promise<void> => {
    // If category is selected, do nothing
    if (categoryId && categoryId.trim() !== "") {
      return;
    }

    // No category - silently delete POI (no toast, no confirmation)
    try {
      // Optimistically remove from Redux
      dispatch(removePOI(poiId));

      // Delete from backend
      await deletePOI({
        surveyId,
        poiId,
      }).unwrap();
    } catch (error) {
      console.error("Failed to delete POI:", error);
      // Rollback Redux removal on failure
      dispatch(updatePOIAction({ extId: poiId, updates: currentPOI }));
      // Silent failure - drawer still closes
    }
  };

  return {
    showCategoryWarning,
    confirmCategoryWarning,
    checkAndDeletePOIWithoutCategory,
  };
};
